import { motion } from 'framer-motion';
import { useInView } from 'react-intersection-observer';
import { PenTool, Network, Activity, HardHat, ShieldAlert, Settings, CheckCircle2 } from 'lucide-react';
import { mockData } from '../mock';

const iconMap = [
  PenTool,
  Network,
  Activity,
  HardHat,
  ShieldAlert,
  Settings
];

export const Services = () => {
  return (
    <section id="services" className="relative py-32 bg-slate-50">
      <div className="max-w-7xl mx-auto px-6">
        {/* Section Header */}
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: "-100px" }}
          transition={{ duration: 0.8 }}
          className="text-center mb-20"
        >
          <h2 className="text-5xl md:text-6xl font-bold text-slate-950 mb-6">
            Our Comprehensive Services
          </h2>
          <div className="w-24 h-1 bg-gradient-to-r from-amber-400 to-amber-500 mx-auto" />
        </motion.div>

        {/* Services Grid - Numbered Manifesto Style */}
        <div className="space-y-32">
          {mockData.services.map((service, index) => {
            const Icon = iconMap[index];
            return <ServiceCard key={index} service={service} icon={Icon} index={index} />;
          })}
        </div>
      </div>
    </section>
  );
};

const ServiceCard = ({ service, icon: Icon, index }) => {
  const [ref, inView] = useInView({
    threshold: 0.2,
    triggerOnce: true
  });

  const isEven = index % 2 === 0;

  return (
    <motion.div
      ref={ref}
      initial={{ opacity: 0, y: 60 }}
      animate={inView ? { opacity: 1, y: 0 } : {}}
      transition={{ duration: 0.8, delay: 0.2 }}
      className={`flex flex-col md:flex-row gap-8 md:gap-16 items-center ${!isEven ? 'md:flex-row-reverse' : ''}`}
    >
      {/* Number and Icon */}
      <motion.div
        initial={{ opacity: 0, scale: 0.8 }}
        animate={inView ? { opacity: 1, scale: 1 } : {}}
        transition={{ duration: 0.6, delay: 0.3 }}
        className="relative md:w-[32%] flex-shrink-0"
      >
        <div className="relative group flex items-center justify-center min-h-[200px]">
          {/* Large Number Background */}
          <div className="text-[140px] md:text-[180px] font-bold text-slate-200 leading-none select-none">
            {service.number}
          </div>
          
          {/* Icon Overlay */}
          <motion.div
            whileHover={{ scale: 1.1, rotate: 5 }}
            className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-20 h-20 md:w-24 md:h-24 bg-gradient-to-br from-amber-400 to-amber-500 rounded-2xl flex items-center justify-center shadow-2xl shadow-amber-500/40 group-hover:shadow-amber-500/60 transition-all duration-300"
          >
            <Icon className="w-10 h-10 md:w-12 md:h-12 text-slate-950" strokeWidth={1.5} />
          </motion.div>
        </div>
      </motion.div>

      {/* Content */}
      <motion.div
        initial={{ opacity: 0, x: isEven ? 30 : -30 }}
        animate={inView ? { opacity: 1, x: 0 } : {}}
        transition={{ duration: 0.8, delay: 0.4 }}
        className="md:w-[68%] flex-1"
      >
        <h3 className="text-4xl md:text-5xl font-bold text-slate-950 mb-6 leading-tight">
          {service.title}
        </h3>
        <p className="text-lg text-slate-600 mb-8 leading-relaxed">
          {service.description}
        </p>
        
        {/* Features List */}
        <div className="space-y-3">
          {service.features.map((feature, i) => (
            <motion.div
              key={i}
              initial={{ opacity: 0, x: -20 }}
              animate={inView ? { opacity: 1, x: 0 } : {}}
              transition={{ duration: 0.5, delay: 0.5 + i * 0.1 }}
              className="flex items-start gap-3 group"
            >
              <CheckCircle2 className="w-6 h-6 text-amber-500 flex-shrink-0 mt-0.5 group-hover:scale-110 transition-transform duration-300" />
              <span className="text-slate-700 font-medium">{feature}</span>
            </motion.div>
          ))}
        </div>
      </motion.div>
    </motion.div>
  );
};
